import { devLog, devError } from '../utils/devLogger';
import { isRealUser } from '../utils/userFilters';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import NotificationService from './notificationService';
import { normalizeRole } from './roleManagementService';

/**
 * Facilitator Service - Firebase operations for facilitator functionality
 * Handles modules, assignments, cohorts, attendance, and progress reporting
 */
class FacilitatorService {
  
  // =========================
  // MODULE MANAGEMENT
  // =========================
  
  /**
   * Create a new module
   * @param {Object} moduleData - Module information
   * @returns {Promise<string>} - Created module ID
   */
  async createModule(moduleData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const module = {
        ...moduleData,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft',
        lessons: moduleData.lessons || [],
        assignments: []
      };

      const docRef = await addDoc(collection(db, 'modules'), module);
      devLog('Module created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      devError('Error creating module:', error);
      throw error;
    }
  }

  /**
   * Get all modules (with optional filtering by creator)
   * @param {string} creatorId - Optional creator ID filter
   * @returns {Promise<Array>} - Array of modules
   */
  async getModules(creatorId = null) {
    try {
      let q;
      if (creatorId) {
        q = query(
          collection(db, 'modules'), 
          where('createdBy', '==', creatorId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(collection(db, 'modules'), orderBy('createdAt', 'desc'));
      }

      const querySnapshot = await getDocs(q);
      const modules = [];
      querySnapshot.forEach((doc) => {
        modules.push({ id: doc.id, ...doc.data() });
      });

      return modules;
    } catch (error) {
      devError('Error fetching modules:', error);
      throw error;
    }
  }

  /**
   * Update a module
   * @param {string} moduleId - Module ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<void>}
   */
  async updateModule(moduleId, updateData) {
    try {
      const moduleRef = doc(db, 'modules', moduleId);
      await updateDoc(moduleRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      devLog('Module updated:', moduleId);
    } catch (error) {
      devError('Error updating module:', error);
      throw error;
    }
  }

  /**
   * Add a lesson to a module
   * @param {string} moduleId - Module ID
   * @param {Object} lessonData - Lesson information
   * @returns {Promise<void>}
   */
  async addLessonToModule(moduleId, lessonData) {
    try {
      const moduleRef = doc(db, 'modules', moduleId);
      const moduleDoc = await getDoc(moduleRef);
      
      if (!moduleDoc.exists()) {
        throw new Error('Module not found');
      }

      const moduleData = moduleDoc.data();
      const lessons = moduleData.lessons || [];
      
      const newLesson = {
        ...lessonData,
        id: `lesson_${Date.now()}`,
        moduleId: moduleId,
        createdAt: new Date().toISOString()
      };

      lessons.push(newLesson);

      await updateDoc(moduleRef, {
        lessons: lessons,
        updatedAt: serverTimestamp()
      });

      devLog('Lesson added to module:', moduleId);
      return newLesson.id;
    } catch (error) {
      devError('Error adding lesson to module:', error);
      throw error;
    }
  }

  // =========================
  // ASSIGNMENT MANAGEMENT
  // =========================

  /**
   * Create a new assignment
   * @param {Object} assignmentData - Assignment information
   * @returns {Promise<string>} - Created assignment ID
   */
  async createAssignment(assignmentData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const assignment = {
        ...assignmentData,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        assignedDate: serverTimestamp(),
        submissions: {},
        status: 'active'
      };

      const docRef = await addDoc(collection(db, 'assignments'), assignment);
      devLog('Assignment created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      devError('Error creating assignment:', error);
      throw error;
    }
  }

  /**
   * Get assignments for a module
   * @param {string} moduleId - Module ID
   * @returns {Promise<Array>} - Array of assignments
   */
  async getAssignmentsByModule(moduleId) {
    try {
      const q = query(
        collection(db, 'assignments'), 
        where('moduleId', '==', moduleId),
        orderBy('dueDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const assignments = [];
      querySnapshot.forEach((doc) => {
        assignments.push({ id: doc.id, ...doc.data() });
      });

      return assignments;
    } catch (error) {
      devError('Error fetching assignments:', error);
      throw error;
    }
  }

  /**
   * Update assignment
   * @param {string} assignmentId - Assignment ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<void>}
   */
  async updateAssignment(assignmentId, updateData) {
    try {
      const assignmentRef = doc(db, 'assignments', assignmentId);
      await updateDoc(assignmentRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      });
      devLog('Assignment updated:', assignmentId);
    } catch (error) {
      devError('Error updating assignment:', error);
      throw error;
    }
  }

  // =========================
  // COHORT MANAGEMENT
  // =========================

  /**
   * Create a new cohort
   * @param {Object} cohortData - Cohort information
   * @returns {Promise<string>} - Created cohort ID
   */
  async createCohort(cohortData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const cohort = {
        ...cohortData,
        createdBy: currentUser.uid,
        facilitators: [currentUser.uid],
        learners: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active'
      };

      const docRef = await addDoc(collection(db, 'cohorts'), cohort);
      devLog('Cohort created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      devError('Error creating cohort:', error);
      throw error;
    }
  }

  /**
   * Get cohorts managed by facilitator
   * @param {string} facilitatorId - Facilitator ID
   * @returns {Promise<Array>} - Array of cohorts
   */
  async getCohortsByFacilitator(facilitatorId) {
    try {
      const q = query(
        collection(db, 'cohorts'), 
        where('facilitators', 'array-contains', facilitatorId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const cohorts = [];
      querySnapshot.forEach((doc) => {
        cohorts.push({ id: doc.id, ...doc.data() });
      });

      return cohorts;
    } catch (error) {
      devError('Error fetching cohorts:', error);
      throw error;
    }
  }

  /**
   * Add learner to cohort
   * @param {string} cohortId - Cohort ID
   * @param {string} learnerId - Learner ID
   * @returns {Promise<void>}
   */
  async addLearnerToCohort(cohortId, learnerId) {
    try {
      const cohortRef = doc(db, 'cohorts', cohortId);
      const cohortDoc = await getDoc(cohortRef);
      
      if (!cohortDoc.exists()) {
        throw new Error('Cohort not found');
      }

      const cohortData = cohortDoc.data();
      const learners = cohortData.learners || [];
      
      if (!learners.includes(learnerId)) {
        learners.push(learnerId);
        
        await updateDoc(cohortRef, {
          learners: learners,
          updatedAt: serverTimestamp()
        });

        // Update user's cohortId
        const userRef = doc(db, 'users', learnerId);
        await updateDoc(userRef, {
          cohortId: cohortId,
          updatedAt: serverTimestamp()
        });

        devLog('Learner added to cohort:', learnerId, cohortId);
      }
    } catch (error) {
      devError('Error adding learner to cohort:', error);
      throw error;
    }
  }

  // =========================
  // ATTENDANCE MANAGEMENT
  // =========================

  /**
   * Mark attendance for multiple students
   * @param {Array} attendanceRecords - Array of attendance records
   * @returns {Promise<void>}
   */
  async markAttendance(attendanceRecords) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const batch = writeBatch(db);

      attendanceRecords.forEach((record) => {
        const attendanceData = {
          ...record,
          markedBy: currentUser.uid,
          markedAt: serverTimestamp(),
          source: 'manual'
        };

        const docRef = doc(collection(db, 'attendance'));
        batch.set(docRef, attendanceData);

        // Also update user subcollection
        const userAttendanceRef = doc(db, 'users', record.learnerId, 'attendance', docRef.id);
        batch.set(userAttendanceRef, attendanceData);
      });

      await batch.commit();
      devLog('Attendance marked for', attendanceRecords.length, 'students');
    } catch (error) {
      devError('Error marking attendance:', error);
      throw error;
    }
  }

  /**
   * Get attendance records for a cohort and date range
   * @param {string} cohortId - Cohort ID
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Array>} - Array of attendance records
   */
  async getAttendanceRecords(cohortId, startDate, endDate) {
    try {
      const q = query(
        collection(db, 'attendance'),
        where('cohortId', '==', cohortId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const records = [];
      querySnapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() });
      });

      return records;
    } catch (error) {
      devError('Error fetching attendance records:', error);
      throw error;
    }
  }

  // =========================
  // PROGRESS REPORTING
  // =========================

  /**
   * Create progress report
   * @param {Object} reportData - Report information
   * @returns {Promise<string>} - Created report ID
   */
  async createProgressReport(reportData) {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const report = {
        ...reportData,
        facilitatorId: currentUser.uid,
        generatedById: currentUser.uid,
        generatedBy: currentUser.email || reportData?.createdByName || 'Unknown',
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'progressReports'), report);
      devLog('Progress report created with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      devError('Error creating progress report:', error);
      throw error;
    }
  }

  /**
   * Get progress reports by facilitator
   * @param {string} facilitatorId - Facilitator ID
   * @returns {Promise<Array>} - Array of progress reports
   */
  async getProgressReports(facilitatorId) {
    try {
      const q = query(
        collection(db, 'progressReports'),
        where('facilitatorId', '==', facilitatorId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reports = [];
      querySnapshot.forEach((doc) => {
        reports.push({ id: doc.id, ...doc.data() });
      });

      return reports;
    } catch (error) {
      devError('Error fetching progress reports:', error);
      throw error;
    }
  }

  // =========================
  // USER MANAGEMENT
  // =========================

  /**
   * Get learners in facilitator's cohorts
   * @param {string} facilitatorId - Facilitator ID
   * @returns {Promise<Array>} - Array of learners
   */
  async getManagedLearners(facilitatorId) {
    try {
      // First get facilitator's cohorts
      const cohorts = await this.getCohortsByFacilitator(facilitatorId);
      const cohortIds = cohorts.map(c => c.id);

      if (cohortIds.length === 0) return [];

      // Get learners in these cohorts
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'learner'),
        where('cohortId', 'in', cohortIds)
      );

      const querySnapshot = await getDocs(q);
      const learners = [];
      querySnapshot.forEach((doc) => {
        const learner = { id: doc.id, ...doc.data() };
        if (isRealUser(learner)) learners.push(learner);
      });

      return learners;
    } catch (error) {
      devError('Error fetching managed learners:', error);
      throw error;
    }
  }

  /**
   * Get learners based on facilitator's role and assignments
   * @param {string} facilitatorId - Facilitator ID
   * @returns {Promise<Array>} - Array of learners accessible to this facilitator
   */
  async getLearnersByAssignedPrograms(facilitatorId, options = {}) {
    try {
      devLog('🔍 Getting learners for facilitator:', facilitatorId);
      const includeDocuments = options.includeDocuments !== false;

      const facilitatorDoc = await getDoc(doc(db, 'users', facilitatorId));
      if (!facilitatorDoc.exists()) {
        throw new Error('Facilitator not found');
      }

      const facilitatorData = facilitatorDoc.data();
      const cacheKey = [
        facilitatorId,
        facilitatorData.role,
        (facilitatorData.assignedPrograms || []).join(','),
        (facilitatorData.assignedCohorts || []).join(','),
        includeDocuments ? 'withDocs' : 'noDocs'
      ].join('|');

      if (!this._learnersCache) {
        this._learnersCache = new Map();
      }
      const cached = this._learnersCache.get(cacheKey);
      if (cached && (Date.now() - cached.at) < 60000) {
        return cached.data;
      }

      devLog('👨‍🏫 Facilitator data:', {
        role: facilitatorData.role,
        assignedPrograms: facilitatorData.assignedPrograms,
        assignedCohorts: facilitatorData.assignedCohorts,
        email: facilitatorData.email
      });

      const learnersMap = new Map();

      // Helper to process query snapshots and add to map
      const populateDocuments = async (learners) => {
        if (!includeDocuments || learners.length === 0) return;
        const chunkSize = 10;
        for (let i = 0; i < learners.length; i += chunkSize) {
          const chunk = learners.slice(i, i + chunkSize);
          const docsResults = await Promise.all(
            chunk.map((learner) =>
              this.getLearnerDocuments(learner.id).catch(() => ({}))
            )
          );
          chunk.forEach((learner, index) => {
            learner.documents = docsResults[index];
          });
        }
      };

      const processSnapshot = async (snapshot) => {
        const newLearners = [];
        for (const doc of snapshot.docs) {
          if (!learnersMap.has(doc.id)) {
            const learnerData = { id: doc.id, ...doc.data() };
            if (!isRealUser(learnerData)) continue;
            learnersMap.set(doc.id, learnerData);
            newLearners.push(learnerData);
          }
        }
        if (newLearners.length > 0) {
          await populateDocuments(newLearners);
        }
      };

      const normalizedRole = normalizeRole(facilitatorData.role);

      // Admin or QA role: Get all learners
      if (['admin', 'qa'].includes(normalizedRole)) {
        const q = query(collection(db, 'users'), where('role', '==', 'learner'));
        const querySnapshot = await getDocs(q);
        await processSnapshot(querySnapshot);
        devLog('👑 Admin/QA access - returning all learners:', learnersMap.size);
        const result = Array.from(learnersMap.values());
        this._learnersCache.set(cacheKey, { at: Date.now(), data: result });
        return result;
      }

      // External viewer: cohort/program scoped access
      if (normalizedRole === 'external_viewer_reporting') {
        const assignedCohorts = facilitatorData.assignedCohorts || facilitatorData.managedCohorts || [];
        const assignedPrograms = facilitatorData.assignedPrograms || [];

        const cohortsRef = collection(db, 'cohorts');
        const cohorts = [];

        const loadCohortsByIds = async (ids) => {
          if (ids.length === 0) return;
          const chunks = [];
          for (let i = 0; i < ids.length; i += 10) {
            chunks.push(ids.slice(i, i + 10));
          }
          for (const chunk of chunks) {
            const cohortQuery = query(cohortsRef, where('__name__', 'in', chunk));
            const snapshot = await getDocs(cohortQuery);
            snapshot.docs.forEach((doc) => cohorts.push({ id: doc.id, ...doc.data() }));
          }
        };

        const loadCohortsByPrograms = async (programIds) => {
          if (programIds.length === 0) return;
          const chunks = [];
          for (let i = 0; i < programIds.length; i += 10) {
            chunks.push(programIds.slice(i, i + 10));
          }
          for (const chunk of chunks) {
            const cohortQuery = query(cohortsRef, where('programId', 'in', chunk));
            const snapshot = await getDocs(cohortQuery);
            snapshot.docs.forEach((doc) => cohorts.push({ id: doc.id, ...doc.data() }));
          }
        };

        await loadCohortsByIds(assignedCohorts);
        if (cohorts.length === 0) {
          await loadCohortsByPrograms(assignedPrograms);
        }

        const allowedCohortIds = cohorts.map((cohort) => cohort.id);

        if (allowedCohortIds.length === 0) {
          devLog('⚠️ External viewer has no cohorts assigned');
          return [];
        }

        const runQueryInChunks = async (field, ids) => {
          if (ids.length === 0) return;
          const chunks = [];
          for (let i = 0; i < ids.length; i += 10) {
            chunks.push(ids.slice(i, i + 10));
          }
          for (const chunk of chunks) {
            const q = query(collection(db, 'users'), where('role', '==', 'learner'), where(field, 'in', chunk));
            const snapshot = await getDocs(q);
            await processSnapshot(snapshot);
          }
        };

        await runQueryInChunks('cohortId', allowedCohortIds);

        devLog('📊 External viewer access results:', {
          accessibleLearners: learnersMap.size,
          allowedCohortIds
        });

        const result = Array.from(learnersMap.values());
        this._learnersCache.set(cacheKey, { at: Date.now(), data: result });
        return result;
      }

      // Facilitator role: Get learners based on program/cohort assignments
      if (normalizedRole === 'facilitator') {
        const assignedPrograms = facilitatorData.assignedPrograms || [];
        const assignedCohorts = facilitatorData.assignedCohorts || [];

        devLog('📋 Checking assignments:', { assignedPrograms, assignedCohorts });

        if (assignedPrograms.length === 0 && assignedCohorts.length === 0) {
          devLog('⚠️ Facilitator has no program or cohort assignments');
          return [];
        }

        // Function to run queries in chunks of 10 for the 'in' operator
        const runQueryInChunks = async (field, ids) => {
          if (ids.length === 0) return;
          const chunks = [];
          for (let i = 0; i < ids.length; i += 10) {
            chunks.push(ids.slice(i, i + 10));
          }
          for (const chunk of chunks) {
            const q = query(collection(db, 'users'), where('role', '==', 'learner'), where(field, 'in', chunk));
            const snapshot = await getDocs(q);
            await processSnapshot(snapshot);
          }
        };

        // Fetch by assigned programs
        await runQueryInChunks('programId', assignedPrograms);
        
        // Fetch by assigned cohorts
        await runQueryInChunks('cohortId', assignedCohorts);

        devLog('📊 Facilitator access results:', {
          accessibleLearners: learnersMap.size,
          assignedPrograms,
          assignedCohorts
        });

        const result = Array.from(learnersMap.values());
        this._learnersCache.set(cacheKey, { at: Date.now(), data: result });
        return result;
      }

      // External facilitator (limited): program/cohort scoped access only.
      if (normalizedRole === 'external_facilitator_limited') {
        const assignedPrograms = facilitatorData.assignedPrograms || [];
        const assignedCohorts = facilitatorData.assignedCohorts || [];

        if (assignedPrograms.length === 0 && assignedCohorts.length === 0) {
          devLog('⚠️ External facilitator has no program or cohort assignments');
          return [];
        }

        const runQueryInChunks = async (field, ids) => {
          if (ids.length === 0) return;
          const chunks = [];
          for (let i = 0; i < ids.length; i += 10) {
            chunks.push(ids.slice(i, i + 10));
          }
          for (const chunk of chunks) {
            const q = query(collection(db, 'users'), where('role', '==', 'learner'), where(field, 'in', chunk));
            const snapshot = await getDocs(q);
            await processSnapshot(snapshot);
          }
        };

        await runQueryInChunks('programId', assignedPrograms);
        await runQueryInChunks('cohortId', assignedCohorts);

        devLog('📊 External facilitator access results:', {
          accessibleLearners: learnersMap.size,
          assignedPrograms,
          assignedCohorts
        });

        const result = Array.from(learnersMap.values());
        this._learnersCache.set(cacheKey, { at: Date.now(), data: result });
        return result;
      }

      devLog('❌ Role not authorized for learner access:', facilitatorData.role);
      return [];

    } catch (error) {
      devError('Error fetching learners by assigned programs:', error);
      throw error;
    }
  }

  /**
   * Update learner status
   * @param {string} learnerId - Learner ID
   * @param {Object} statusUpdate - Status update data
   * @returns {Promise<void>}
   */
  async updateLearnerStatus(learnerId, statusUpdate) {
    try {
      const userRef = doc(db, 'users', learnerId);
      await updateDoc(userRef, {
        ...statusUpdate,
        updatedAt: serverTimestamp()
      });
      devLog('Learner status updated:', learnerId);
    } catch (error) {
      devError('Error updating learner status:', error);
      throw error;
    }
  }

  /**
   * Get learner documents
   * @param {string} learnerId - Learner ID
   * @returns {Promise<Object>} - Object with documents keyed by category
   */
  async getLearnerDocuments(learnerId) {
    try {
      const documentsRef = collection(db, 'users', learnerId, 'documents');
      const documentsSnapshot = await getDocs(documentsRef);

      const documents = {};
      const getDocumentStatus = (data = {}) => {
        if (data.reviewStatus === 'qa_approved') return 'verified';
        if (data.reviewStatus === 'qa_rejected') return 'rejected';
        if (data.status === 'approved') return 'verified';
        return data.status || 'pending';
      };

      const toDateSafe = (value) => {
        if (!value) return null;
        if (typeof value?.toDate === 'function') return value.toDate();
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };

      const getActivityTime = (data = {}) => (
        toDateSafe(data.reviewedAt)?.getTime() ||
        toDateSafe(data.uploadedAt)?.getTime() ||
        toDateSafe(data.uploadDate)?.getTime() ||
        toDateSafe(data.updatedAt)?.getTime() ||
        0
      );

      documentsSnapshot.forEach((docSnapshot) => {
        const docData = docSnapshot.data();
        const docId = docSnapshot.id;
        if (docData.deleted === true) {
          return;
        }

        const document = {
          id: docId,
          ...docData,
          status: getDocumentStatus(docData)
        };

        // Use the primary key based on priority: categoryKey > categoryId > category name
        // This ensures we only add each document ONCE under its primary key
        const primaryKey = docData.categoryKey || docData.categoryId || docData.category;

        if (primaryKey) {
          const existing = documents[primaryKey];
          if (!existing || getActivityTime(document) >= getActivityTime(existing)) {
            documents[primaryKey] = document;
          }
        }
      });

      devLog(`📄 Loaded ${Object.keys(documents).length} unique documents for learner ${learnerId}:`, Object.keys(documents));
      return documents;
    } catch (error) {
      devError('Error fetching learner documents:', error);
      return {}; // Return empty object if error occurs
    }
  }

  // =========================
  // DOCUMENT MANAGEMENT
  // =========================

  /**
   * Get filtered resources for a facilitator
   * @param {string} facilitatorId - The ID of the facilitator
   * @returns {Promise<Array>} - A promise that resolves to an array of resources
   */
  async getResources(facilitatorId) {
    try {
      const facilitatorDoc = await getDoc(doc(db, 'users', facilitatorId));
      if (!facilitatorDoc.exists()) {
        devError('Facilitator not found');
        return [];
      }
      
      const facilitatorData = facilitatorDoc.data();
      const userRole = normalizeRole(facilitatorData.role);
      const assignedPrograms = facilitatorData.assignedPrograms || [];
      const assignedCohorts = facilitatorData.assignedCohorts || [];

      const resourcesRef = collection(db, 'resources');
      const resourcesQuery = query(resourcesRef, orderBy('uploadedAt', 'desc'), limit(200));
      const resourcesSnapshot = await getDocs(resourcesQuery);

      const allResources = resourcesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate() || new Date(),
        releaseAt: doc.data().releaseAt?.toDate() || null
      }));

      const now = new Date();
      const isAdminOrFacilitator = ['admin', 'facilitator'].includes(userRole);
      const isExternalFacilitator = userRole === 'external_facilitator_limited';

      // Filter resources
      const filteredResources = allResources.filter(resource => {
        // Check for scheduled release
        if (!isAdminOrFacilitator && resource.releaseAt && resource.releaseAt > now) {
          return false;
        }

        if (isExternalFacilitator) {
          const cohortIds = Array.isArray(resource.cohortIds)
            ? resource.cohortIds
            : (resource.cohortId ? [resource.cohortId] : []);
          const assignedUsers = resource.assignedExternalUsers || [];
          if (assignedUsers.includes(facilitatorId)) return true;
          if (cohortIds.length === 0) return false;
          return cohortIds.some((id) => assignedCohorts.includes(id));
        }

        // Check for access permissions
        if (resource.accessLevel === 'public' || !resource.accessLevel) return true;
        if (resource.programId && assignedPrograms.includes(resource.programId)) return true;
        if (resource.cohortId && assignedCohorts.includes(resource.cohortId)) return true;
        
        return false;
      });

      return filteredResources;
    } catch (error) {
      devError('Error fetching resources:', error);
      return [];
    }
  }

  /**
   * Verify a learner document
   * @param {string} learnerId - Learner ID
   * @param {string} documentId - Document ID
   * @param {string} status - 'verified' or 'rejected'
   * @param {string} comment - Optional comment for rejection
   * @returns {Promise<void>}
   */
  async verifyDocument(learnerId, documentId, status, comment = '') {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('User not authenticated');

      const documentRef = doc(db, 'users', learnerId, 'documents', documentId);
      await updateDoc(documentRef, {
        status: status,
        reviewedBy: currentUser.uid,
        reviewedAt: serverTimestamp(),
        rejectionComment: status === 'rejected' ? comment : '',
        updatedAt: serverTimestamp()
      });

      await NotificationService.notify(
        'document_status',
        'Document Status Updated',
        `Your document was marked as ${status}.${comment ? ` Comment: ${comment}` : ''}`,
        learnerId,
        { category: 'documents', additionalData: { documentId, status } }
      );

      devLog(`Document ${status}:`, documentId);
    } catch (error) {
      devError('Error verifying document:', error);
      throw error;
    }
  }

  /**
   * Get documents for verification
   * @param {Array} learnerIds - Array of learner IDs
   * @returns {Promise<Array>} - Array of documents needing verification
   */
  async getDocumentsForVerification(learnerIds) {
    try {
      const documents = [];
      
      for (const learnerId of learnerIds) {
        const q = query(
          collection(db, 'users', learnerId, 'documents'),
          where('status', 'in', ['pending', 'rejected'])
        );

        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
          documents.push({ 
            id: doc.id, 
            learnerId: learnerId,
            ...doc.data() 
          });
        });
      }

      return documents;
    } catch (error) {
      devError('Error fetching documents for verification:', error);
      throw error;
    }
  }

  // =========================
  // UTILITY FUNCTIONS
  // =========================

  /**
   * Generate Teams meeting link
   * @returns {string} - Generated Teams link
   */
  generateTeamsLink() {
    const meetingId = Math.random().toString(36).substring(2, 15);
    return `https://teams.microsoft.com/l/meetup-join/19%3ameeting_${meetingId}`;
  }

  /**
   * Calculate notes release time
   * @param {string} sessionDate - Session date
   * @param {string} sessionTime - Session time
   * @param {number} delayMinutes - Delay in minutes
   * @returns {string} - ISO timestamp for notes release
   */
  calculateNotesReleaseTime(sessionDate, sessionTime, delayMinutes = 5) {
    const sessionDateTime = new Date(`${sessionDate}T${sessionTime}`);
    const releaseTime = new Date(sessionDateTime.getTime() + (delayMinutes * 60000));
    return releaseTime.toISOString();
  }
}

// Create and export singleton instance
const facilitatorService = new FacilitatorService();
export default facilitatorService;
